// AGC: Recommendation model versions — publish, list, restore
import React, { useState, useEffect, useCallback } from 'react'
import { History, Upload, RotateCcw, CheckCircle, Loader2, AlertCircle } from 'lucide-react'
import { getBackendUrl } from '../../../utils/backendUrl'
import { useTranslation } from '../../../hooks/useTranslation'
import type { RecommendationModelVersion } from '../../../types/admin/models'

const AUTH_HEADERS = { 'Authorization': 'Bearer admin-token', 'Content-Type': 'application/json' }

export const RecommendationVersions: React.FC = () => {
  const { t } = useTranslation()
  const [versions, setVersions] = useState<RecommendationModelVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [restoring, setRestoring] = useState<string | null>(null)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [label, setLabel] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const loadVersions = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/recommendation-models`, { headers: AUTH_HEADERS })
      const data = await res.json()
      if (data.success) setVersions(data.data.versions || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadVersions() }, [loadVersions])

  const handlePublish = async () => {
    if (!label.trim()) return
    setPublishing(true)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/recommendation-models/publish`, {
        method: 'POST',
        headers: AUTH_HEADERS,
        body: JSON.stringify({ label, notes }),
      })
      const data = await res.json()
      if (data.success) {
        setShowPublishModal(false)
        setLabel('')
        setNotes('')
        await loadVersions()
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setPublishing(false)
    }
  }

  const handleRestore = async (versionId: string) => {
    if (!confirm(t('admin.versions.confirmRestore'))) return
    setRestoring(versionId)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/recommendation-models/${versionId}/restore`, {
        method: 'POST',
        headers: AUTH_HEADERS,
        body: JSON.stringify({}),
      })
      const data = await res.json()
      if (data.success) await loadVersions()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setRestoring(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-indigo-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.versions.title')}</h3>
        </div>
        <button
          onClick={() => setShowPublishModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
        >
          <Upload className="w-4 h-4" />
          {t('admin.versions.publish')}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : versions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">{t('admin.versions.empty')}</div>
      ) : (
        <div className="space-y-2">
          {versions.map(v => (
            <div key={v.versionId} className={`p-4 rounded-xl border-2 ${v.isActive ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white text-sm">{v.label}</span>
                    {v.isActive && (
                      <span className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3" /> {t('admin.versions.active')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{new Date(v.createdAt).toLocaleString()} — {v.createdBy}</p>
                  {v.notes && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{v.notes}</p>}
                </div>
                {!v.isActive && (
                  <button
                    onClick={() => handleRestore(v.versionId)}
                    disabled={restoring === v.versionId}
                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-xs font-medium disabled:opacity-50"
                  >
                    {restoring === v.versionId ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    {t('admin.versions.restore')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AGC: Publish modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('admin.versions.publishTitle')}</h4>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder={t('admin.versions.labelPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('admin.versions.notesPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowPublishModal(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm">
                {t('common.cancel')}
              </button>
              <button
                onClick={handlePublish}
                disabled={!label.trim() || publishing}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('admin.versions.publish')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecommendationVersions
